import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import type { LBNodeInfo } from '../types';
import authoringManifest from '../agent/generatedAuthoringSchema.json';

// Import all NodeInfo definitions - add new nodes here
import { AddCocNoteActionNodeInfo } from './addCocNoteAction/AddCocNoteActionNodeInfo';
import { AICallActionNodeInfo } from './aiCallAction/AICallActionNodeInfo';
import { GetAlertStatusNodeInfo } from './getAlertStatus/GetAlertStatusNodeInfo';
import { AlertTriggerNodeInfo } from './alertTrigger/AlertTriggerNodeInfo';
import { AlertActionTakenTriggerNodeInfo } from './alertActionTakenTrigger/AlertActionTakenTriggerNodeInfo';
import { CallWebhookActionNodeInfo } from './callWebhookAction/CallWebhookActionNodeInfo';
import { ConditionalNodeInfo } from './conditional/ConditionalNodeInfo';
import { DelayActionNodeInfo } from './delayAction/DelayActionNodeInfo';
import { SnoozeActionNodeInfo } from './snoozeAction/SnoozeActionNodeInfo';
import { EmailActionNodeInfo } from './emailAction/EmailActionNodeInfo';
import { Deprecated_FilteredShipmentsLoopNodeInfo } from './deprecatedFilteredShipmentsLoop/Deprecated_FilteredShipmentsLoopNodeInfo';
import { PathsNodeInfo } from './paths/PathsNodeInfo';
import { ResolveAlertActionNodeInfo } from './resolveAlertAction/ResolveAlertActionNodeInfo';
// DEPRECATED: superseded by ScheduleTriggerV2, kept so existing workflows render.
import { ScheduleTriggerNodeInfo } from './scheduleTrigger/ScheduleTriggerNodeInfo';
import { SlackActionNodeInfo } from './slackAction/SlackActionNodeInfo';
import { SmsActionNodeInfo } from './smsAction/SmsActionNodeInfo';
import { LocationTriggerNodeInfo } from './locationTrigger/LocationTriggerNodeInfo';
import { ETAChangeTriggerNodeInfo } from './etaTrigger/ETAChangeTriggerNodeInfo';
import { StatusProgressTriggerNodeInfo } from './statusProgressTrigger/StatusProgressTriggerNodeInfo';
import { manualExecutionTriggerNodeInfo } from './manualExecutionTrigger/manualExecutionTriggerNodeInfo';
import { InboundTriggerNodeInfo } from './inboundTrigger/InboundTriggerNodeInfo';
// DEPRECATED: kept so existing workflows using the old node types continue to render.
// Remove after all published InboundCallTrigger / InboundEventTrigger workflows are migrated.
import { InboundCallTriggerNodeInfo } from './inboundCallTrigger/InboundCallTriggerNodeInfo';
import { InboundEventTriggerNodeInfo } from './inboundEventTrigger/InboundEventTriggerNodeInfo';
import { ScheduleTriggerV2NodeInfo } from './scheduleTriggerV2/ScheduleTriggerV2NodeInfo';
import { FilterControlFlowNodeInfo } from './filterControlFlow/FilterControlFlowNodeInfo';
import { TeamsNotificationNodeInfo } from './teamsNotification/TeamsNotificationNodeInfo';
import { TransferToActionNodeInfo } from './transferToAction/TransferToActionNodeInfo';
import { WaitForReplyNodeInfo } from './waitForReply/WaitForReplyNodeInfo';

export type NodeType = keyof LBNodeData;

// Generated lifecycle data is shared with Copilot capability negotiation.
// Existing deprecated variants remain registered so saved workflows render.
const PRODUCTION_READY_NODE_TYPES = new Set<string>(
  authoringManifest.creatable_node_types
);

export function isProductionReadyNodeType(nodeType: NodeType): boolean {
  return PRODUCTION_READY_NODE_TYPES.has(nodeType);
}

/**
 * Builds the list of all NodeInfos.
 *
 * This is a function (not a top-level const) to avoid circular-import crashes.
 * If this module were imported transitively by a NodeInfo during that NodeInfo's
 * own initialization, a top-level array literal would capture `undefined` for the
 * mid-loading NodeInfo — and the first read of `.protobufNodeDataKey` on that
 * entry would throw. Deferring construction until the first runtime call means
 * every NodeInfo has finished loading before we dereference any of them.
 */
function buildAllNodeInfos(): readonly LBNodeInfo[] {
  return [
    AddCocNoteActionNodeInfo,
    AICallActionNodeInfo,
    GetAlertStatusNodeInfo,
    AlertTriggerNodeInfo,
    AlertActionTakenTriggerNodeInfo,
    LocationTriggerNodeInfo,
    ETAChangeTriggerNodeInfo,
    CallWebhookActionNodeInfo,
    ConditionalNodeInfo,
    DelayActionNodeInfo,
    SnoozeActionNodeInfo,
    EmailActionNodeInfo,
    Deprecated_FilteredShipmentsLoopNodeInfo,
    PathsNodeInfo,
    ResolveAlertActionNodeInfo,
    ScheduleTriggerNodeInfo, // DEPRECATED: legacy, kept so existing workflows render
    SlackActionNodeInfo,
    SmsActionNodeInfo,
    StatusProgressTriggerNodeInfo,
    manualExecutionTriggerNodeInfo,
    InboundTriggerNodeInfo,
    InboundCallTriggerNodeInfo, // DEPRECATED: legacy, kept so existing workflows render
    InboundEventTriggerNodeInfo, // DEPRECATED: legacy, kept so existing workflows render
    ScheduleTriggerV2NodeInfo,
    FilterControlFlowNodeInfo,
    TeamsNotificationNodeInfo,
    TransferToActionNodeInfo,
    WaitForReplyNodeInfo,
  ];
}

let _allNodeInfos: readonly LBNodeInfo[] | undefined;
let _nodeTypeToInfo: Record<NodeType, LBNodeInfo> | undefined;

/** Returns every registered NodeInfo. Memoized on first call. */
export function getAllNodeInfos(): readonly LBNodeInfo[] {
  if (!_allNodeInfos) {
    _allNodeInfos = buildAllNodeInfos();
  }
  return _allNodeInfos;
}

function getNodeTypeToInfo(): Record<NodeType, LBNodeInfo> {
  if (!_nodeTypeToInfo) {
    _nodeTypeToInfo = Object.fromEntries(
      getAllNodeInfos().map((info) => [info.protobufNodeDataKey, info])
    ) as Record<NodeType, LBNodeInfo>;
  }
  return _nodeTypeToInfo;
}

/**
 * Determines the node type from LBNodeData by checking which dataKey is present.
 */
export function getLBNodeType(data: LBNodeData): NodeType | undefined {
  for (const info of getAllNodeInfos()) {
    if (data?.[info.protobufNodeDataKey]) {
      return info.protobufNodeDataKey;
    }
  }
  return undefined;
}

/**
 * Returns info for a Logiblock node based on its data.
 */
export function getLBNodeInfo(data: LBNodeData): LBNodeInfo | undefined {
  const nodeType = getLBNodeType(data);
  return nodeType ? getNodeTypeToInfo()[nodeType] : undefined;
}

/**
 * Returns info for a Logiblock node based on its data. Throws if the data has no node type set.
 */
export function requireLBNodeInfo(data: LBNodeData): LBNodeInfo {
  const nodeInfo = getLBNodeInfo(data);
  if (nodeInfo === undefined) {
    throw new Error('Node data has no node type set.');
  }
  return nodeInfo;
}

export function getNodeInfoForType(nodeType: NodeType): LBNodeInfo {
  return getNodeTypeToInfo()[nodeType];
}

/**
 * Reports whether a name is the node data key of a registered node type.
 * Use this to check names that arrive at runtime, such as a node type named
 * in a plan from the workflow agent.
 */
export function isNodeType(name: string): name is NodeType {
  return Object.hasOwn(getNodeTypeToInfo(), name);
}

/**
 * The label to show on the node card and customize header.
 * Returns the user-provided custom name if one is set, otherwise the
 * node type's default label. Unset or empty `custom_display_name` both
 * mean "no custom name".
 */
export function getNodeDisplayName(
  nodeData: LBNodeData,
  nodeInfo: LBNodeInfo
): string {
  const custom = nodeData.custom_display_name;
  return custom !== undefined && custom !== '' ? custom : nodeInfo.label;
}
